import React from 'react';
import { SchematicShell, Rect, Line } from './SchematicPrimitives';

/**
 * Elevation schematic for `television`.
 *
 * Front: bezel-framed screen with a small centered logo stripe at the
 * bottom.
 * Side: a thin (~4") edge-on profile of the panel plus a small wall-
 * mount bracket behind it. Right mirrors left.
 *
 * Matches `tvRenderer` in furnitureRenderers.js: full-bbox panel,
 * ~0.4" bezel, glossy screen recessed inside the bezel.
 */

const INCH = 0.0254;

export function TelevisionSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right' ? `scale(-1, 1) translate(${-depth}, 0)` : ''
          }
        >
          <TVSideProfile
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT
  const bezel = Math.min(0.6 * INCH, Math.min(width, height) * 0.04);
  const screenX = bezel;
  const screenY = bezel;
  const screenW = Math.max(0, width - bezel * 2);
  const screenH = Math.max(0, height - bezel * 2);
  // Logo stripe — short horizontal segment centered at the bottom of the
  // bezel band.
  const logoY = height - bezel / 2;
  const logoLen = Math.min(width * 0.18, 4 * INCH);

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Screen inset within the bezel */}
      {screenW > 0 && screenH > 0 && (
        <Rect
          x={screenX}
          y={screenY}
          width={screenW}
          height={screenH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
          fill={strokeColor}
          fillOpacity={0.12}
        />
      )}
      {/* Logo stripe at the bottom of the bezel */}
      <Line
        x1={width / 2 - logoLen / 2}
        y1={logoY}
        x2={width / 2 + logoLen / 2}
        y2={logoY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

function TVSideProfile({ depth, height, strokeColor, strokeWidth }) {
  // Panel is a thin slab at the front (the +Z face of the bbox). The
  // wall-mount bracket sits behind it. Cap depth-driven thickness so it
  // still reads when the data dimension is unusually deep.
  const panelThick = Math.min(depth * 0.6, 2 * INCH);
  // Front of the TV in this view is at x = depth (so the slim panel sits
  // against the front face). The room/wall is at x = 0.
  const panelX = depth - panelThick;
  return (
    <g>
      {/* Panel slab */}
      <Rect
        x={panelX}
        y={0}
        width={panelThick}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        fill={strokeColor}
        fillOpacity={0.12}
      />
      {/* Wall-mount bracket — small rectangle centered on panel back */}
      <Rect
        x={0}
        y={height * 0.35}
        width={Math.min(depth * 0.45, 6 * INCH)}
        height={height * 0.3}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Short connector line from bracket to panel */}
      <Line
        x1={Math.min(depth * 0.45, 6 * INCH)}
        y1={height / 2}
        x2={panelX}
        y2={height / 2}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

TelevisionSchematic.supportsSideViews = true;
